import type { ApiError, ContentDetail, ContentListItem, SetupStatus } from "@zzirasi/contracts";
import { isNativeApp, secureDelete, secureGet, secureSet, unregisterFromPushNotifications } from "@zzirasi/android/native-bridge";

let appAccessToken: string | null = null;
const apiOrigin = () => localStorage.getItem("zzirasi_api_origin") ?? "";
export const nativeRuntime = isNativeApp();
export const absoluteApiUrl = (path: string): string => `${apiOrigin()}${path.startsWith("/") ? path : `/${path}`}`;

export function configuredServerOrigin(): string | null {
  return localStorage.getItem("zzirasi_api_origin");
}

export function setServerOrigin(origin: string): void {
  const parsed = new URL(origin);
  if (parsed.protocol !== "https:" && !(import.meta.env.DEV && parsed.protocol === "http:")) throw new Error("HTTPS 서버 주소가 필요합니다.");
  localStorage.setItem("zzirasi_api_origin", parsed.origin);
}

async function refreshAppSession(): Promise<boolean> {
  if (!nativeRuntime) return false;
  const refreshToken = await secureGet("refresh-token");
  if (!refreshToken) return false;
  const response = await fetch(`${apiOrigin()}/api/v1/auth/app/refresh`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refreshToken }) });
  if (!response.ok) { await secureDelete("refresh-token"); appAccessToken = null; return false; }
  const tokens = await response.json() as { accessToken: string; refreshToken: string };
  appAccessToken = tokens.accessToken;
  await secureSet("refresh-token", tokens.refreshToken);
  return true;
}

export async function initializePlatformSession(): Promise<void> {
  if (nativeRuntime && configuredServerOrigin()) await refreshAppSession();
}

export class RequestError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); }
}

export async function api<T>(path: string, init: RequestInit = {}, retryAuth = true): Promise<T> {
  const response = await fetch(`${apiOrigin()}/api/v1${path}`, {
    ...init,
    credentials: nativeRuntime ? "omit" : "include",
    headers: { ...(init.body !== undefined ? { "content-type": "application/json" } : {}), ...(appAccessToken ? { authorization: `Bearer ${appAccessToken}` } : {}), ...init.headers }
  });
  if (response.status === 401 && nativeRuntime && retryAuth && await refreshAppSession()) return api<T>(path, init, false);
  if (response.status === 204) return undefined as T;
  const body = await response.json() as T | ApiError;
  if (!response.ok) {
    const error = (body as ApiError).error;
    throw new RequestError(response.status, error?.code ?? "UNKNOWN", error?.message ?? "요청에 실패했습니다.");
  }
  return body as T;
}

export const client = {
  setupStatus: () => api<SetupStatus>("/setup/status"),
  testSetupDatabase: (body: unknown) => api<{ ok: true; databaseClient: "sqlite3" | "pg" | "mysql2" }>("/setup/database/test", { method: "POST", body: JSON.stringify(body) }),
  setup: (body: unknown) => api<{ configured: true; installationId: string }>("/setup/complete", { method: "POST", body: JSON.stringify(body) }),
  login: async (body: unknown) => {
    if (!nativeRuntime) return api<{ user: User }>("/auth/local/login", { method: "POST", body: JSON.stringify(body) });
    const result = await api<{ user: User; accessToken: string; refreshToken: string }>("/auth/app/login", { method: "POST", body: JSON.stringify(body) }, false);
    appAccessToken = result.accessToken;
    await secureSet("refresh-token", result.refreshToken);
    return { user: result.user };
  },
  oidcAppStart: () => api<{ authorizationUrl: string; expiresIn: number }>("/auth/oidc/app/start", { method: "POST" }),
  oidcAppExchange: async (code: string, state: string) => {
    const result = await api<{ user: User; accessToken: string; refreshToken: string }>("/auth/oidc/app/exchange", { method: "POST", body: JSON.stringify({ code, state }) }, false);
    appAccessToken = result.accessToken;
    await secureSet("refresh-token", result.refreshToken);
    return result.user;
  },
  logout: async () => {
    const refreshToken = nativeRuntime ? await secureGet("refresh-token") : null;
    const installationId = localStorage.getItem("zzirasi_installation_id");
    try {
      if (nativeRuntime) {
        try {
          if (installationId) await api<void>(`/devices/${encodeURIComponent(installationId)}/push`, { method: "DELETE" }).catch(() => undefined);
          await api<void>("/auth/app/logout", { method: "POST", body: JSON.stringify({ refreshToken }) });
        } finally {
          // Native notification state must be cleared even when the server is unreachable.
          await unregisterFromPushNotifications().catch(() => undefined);
        }
      } else await api<void>("/auth/logout", { method: "POST" });
    } finally {
      appAccessToken = null;
      if (nativeRuntime) await secureDelete("refresh-token");
    }
  },
  me: () => api<{ user: User }>("/me"),
  dashboard: () => api<{ counts: Record<string, number>; publicationFailures: number }>("/dashboard"),
  contents: (search = "", originType = "") => api<{ items: ContentListItem[] }>(`/content-items?${new URLSearchParams({ ...(search ? { search } : {}), ...(originType ? { originType } : {}) }).toString()}`),
  content: (id: string) => api<ContentDetail>(`/content-items/${id}`),
  createContent: (body: unknown) => api<{ id: string }>("/content-items", { method: "POST", body: JSON.stringify(body) }),
  saveContent: (id: string, body: unknown) => api<{ id: string; version: number }>(`/content-items/${id}/versions`, { method: "POST", body: JSON.stringify(body) }),
  uploadMedia: async (id: string, file: File) => {
    const form = new FormData(); form.set("file", file);
    const response = await fetch(`${apiOrigin()}/api/v1/content-items/${id}/media`, { method: "POST", credentials: nativeRuntime ? "omit" : "include", headers: appAccessToken ? { authorization: `Bearer ${appAccessToken}` } : {}, body: form });
    if (!response.ok) { const body = await response.json() as ApiError; throw new RequestError(response.status, body.error.code, body.error.message); }
    return response.json() as Promise<{ id: string }>;
  },
  anonymize: (id: string, body: unknown) => api<{ id: string; version: number }>(`/content-items/${id}/anonymize`, { method: "POST", body: JSON.stringify(body) }),
  changeReviewStatus: (id: string, action: "HOLD" | "REJECT" | "REOPEN", reason: string) => api<{ id: string; status: string }>(`/content-items/${id}/review-status`, { method: "POST", body: JSON.stringify({ action, reason }) }),
  approvalPreview: (id: string) => api<ApprovalPreview>(`/content-items/${id}/approval-preview`, { method: "POST" }),
  approve: (id: string, body: unknown) => api<{ publicationId: string; status: string }>(`/content-items/${id}/approve`, { method: "POST", body: JSON.stringify(body) }),
  bots: () => api<{ bots: Bot[] }>("/telegram-bots"),
  connectBot: (purpose: string, token: string, confirmIdentityChange = false) => api<Bot>(`/telegram-bots/${purpose}/connect`, { method: "POST", body: JSON.stringify({ token, confirmIdentityChange }) }),
  testBot: (purpose: string) => api<BotTestResult>(`/telegram-bots/${purpose}/test`, { method: "POST" }),
  disconnectBot: (purpose: string) => api<void>(`/telegram-bots/${purpose}`, { method: "DELETE", body: JSON.stringify({ confirmed: true }) }),
  createAdminBotLink: () => api<AdminBotLink>("/telegram-bots/ADMIN/link-code", { method: "POST" }),
  sourceSubscriptions: () => api<{ configured: boolean; sources: SourceSubscription[] }>("/source-subscriptions"),
  createSourceSubscription: (input: { displayName: string; telegramHandle: string; connectorType: string }) => api<SourceSubscription>("/source-subscriptions", { method: "POST", body: JSON.stringify({ displayName: input.displayName, identifier: input.telegramHandle, connectorType: input.connectorType }) }),
  updateSourceSubscription: (id: string, input: { status: "ACTIVE" | "PAUSED" }) => api<SourceSubscription>(`/source-subscriptions/${id}`, { method: "PATCH", body: JSON.stringify(input) }),
  testSourceConnection: (id: string) => api<{ ok: boolean; identity?: string; message?: string }>(`/source-subscriptions/${id}/test`, { method: "POST" }),
  settings: () => api<RuntimeSettings>("/settings"),
  updateSettings: (values: Record<string, string>) => api<{ updated: string[]; restartRecommended: boolean }>("/settings", { method: "PUT", body: JSON.stringify({ values }) }),
  testIntegration: (kind: "OIDC" | "TELEGRAM_SOURCE" | "FCM") => api<{ ok: boolean; message: string }>("/settings/test", { method: "POST", body: JSON.stringify({ kind }) }),
  startTelegramUserAuth: (apiId: string, apiHash: string, phoneNumber: string) => api<TelegramAuthFlow>("/settings/telegram-auth/start", { method: "POST", body: JSON.stringify({ apiId, apiHash, phoneNumber }) }),
  submitTelegramUserAuth: (flowId: string, input: { code?: string; password?: string }) => api<TelegramAuthFlow>(`/settings/telegram-auth/${flowId}/submit`, { method: "POST", body: JSON.stringify(input) }),
  telegramUserAuthStatus: (flowId: string) => api<TelegramAuthFlow>(`/settings/telegram-auth/${flowId}`),
  targets: () => api<{ targets: Target[] }>("/targets"),
  addTarget: (chatId: string, displayName: string) => api<Target>("/targets", { method: "POST", body: JSON.stringify({ chatId, displayName }) }),
  audiences: () => api<{ counts: Record<string, number>; subscribers: AudienceSubscriber[] }>("/audiences"),
  publications: () => api<{ publications: Publication[] }>("/publications"),
  editPublication: (id: string, text: string, reason: string) => api<{ id: string; status: string }>(`/publications/${id}/edit`, { method: "POST", body: JSON.stringify({ text, reason, confirmed: true }) }),
  deletePublication: (id: string, reason: string) => api<{ id: string; status: string; failures: number }>(`/publications/${id}/delete`, { method: "POST", body: JSON.stringify({ reason, confirmed: true }) }),
  audit: () => api<{ events: AuditEvent[] }>("/audit"),
  registerPush: (installationId: string, token: string) => api<void>("/devices/register-push", { method: "POST", body: JSON.stringify({ installationId, token, displayName: "ZRS Android" }) })
};

export type User = { id: string; email: string; displayName: string; role: string };
export type AdminBotLink = { code: string; command: string; botUsername: string; expiresAt: string };
export type Bot = {
  id?: string;
  purpose: string;
  display_name?: string;
  displayName?: string;
  username?: string;
  telegram_user_id?: string;
  telegramUserId?: string;
  status: string;
  last_error?: string | null;
  lastError?: string | null;
  last_checked_at_ms?: number | null;
  lastCheckedAt?: string | null;
  webhook_configured?: boolean;
  webhookConfigured?: boolean;
  webhook_url?: string | null;
  webhookUrl?: string | null;
  webhook_pending_update_count?: number;
  webhookPendingUpdateCount?: number;
  webhookMatches?: boolean;
  pendingUpdates?: number;
};
export type BotTestResult = Bot & { webhookMatches?: boolean; pendingUpdates?: number };
export type Target = { id: string; chat_id?: string; chatId?: string; display_name?: string; displayName?: string };
export type ApprovalPreview = { contentId: string; version: number; contentHash: string; telegramText: string; warnings: string[]; expiresAt: string };
export type Publication = { id: string; contentItemId: string; title: string; text: string; status: string; runAt: string; error: string | null };
export type AuditEvent = { id: string; action: string; entityType: string; entityId: string | null; detail: unknown; createdAt: string };
export type SourceSubscription = { id: string; displayName: string; telegramHandle: string; connectorType: "PUBLIC_CHANNEL" | "BOT_TO_BOT" | "ADMIN_FORWARD"; status: "ACTIVE" | "PAUSED" | "DEGRADED" | "ERROR" | "CONNECTING"; lastCollectedAt: string | null; lastError: string | null; collectedCount: number; pendingReviewCount: number };
export type RuntimeSettings = { databaseClient: "sqlite3" | "pg" | "mysql2"; values: Record<string, string | null>; secretsConfigured: Record<string, boolean>; sourceClientConnected: boolean };
export type TelegramAuthFlow = { flowId: string; status: "STARTING" | "CODE_REQUIRED" | "VERIFYING" | "PASSWORD_REQUIRED" | "AUTHORIZED" | "FAILED"; error: string | null; expiresAt: string };
export type AudienceSubscriber = { id: string; anonymousId: string; status: string; consentVersion: string; consentedAt: string | null; updatedAt: string };
